import { CaseStudyDoc } from '../types';

export const CASE_STUDIES: Record<string, CaseStudyDoc> = {
  modulosm: {
    slug: 'modulosm',
    headline: 'building a branding system, ground-up',
    heroGradient:
      'linear-gradient(135deg, #1d4ed8 0%, #3b82f6 28%, #fb923c 62%, #2563eb 100%)',
    heroInner: {
      type: 'video',
      src: '/media/MOSM-vid.mp4',
    },
    meta: {
      role: 'brand designer, web designer',
      team: 'solo case competition (just me!)',
      skills: 'graphic design, ui/ux design, web design',
      timeline: 'feb – march 2026',
    },
    sections: [
      {
        id: 'overview',
        label: 'overview',
        blocks: [
          {
            type: 'p',
            text:
              'ModularOSM (MOSM) is a next-generation off-site manufacturing company specializing in modular data centre assembly. Operating as the "Foxconn of data centres," MOSM builds factory-assembled systems deployed rapidly for clients like Microsoft, Google, Amazon, and Meta. This project was a competition brief to develop a full brand system and website concept for a company at a pivotal stage of growth.',
          },
          {
            type: 'img',
            src: '/media/mosm-logo.svg',
            alt: 'ModularOSM logo on white background',
            squareCorners: true,
          },
          {
            type: 'p',
            text:
              'MOSM had strong technical capability and high-profile clientele, but lacked a cohesive brand identity. The goal was to build a brand system that communicates confidence and credibility while remaining flexible enough to scale with the company and differentiate it within a competitive, enterprise-facing industry.',
          },
        ],
      },
      {
        id: 'the-design-process',
        label: 'the design process',
        blocks: [
          {
            type: 'p',
            text:
              'I began with brand strategy, defining core values and a positioning statement to anchor all creative decisions. This informed logo concept development, colour palette and typography selection, and a scalable visual language system. Website concepts were developed with accessibility and global adaptability in mind, with documented design rationale throughout to support stakeholder alignment.',
          },
          {
            type: 'img',
            src: '/media/mosm-design-process.png',
            alt: 'ModularOSM branding and design system slide overview',
          },
        ],
      },
      {
        id: 'initial-research',
        label: 'initial research',
        blocks: [
          {
            type: 'p',
            text:
              'Before designing, I began my initial research, which would ground my creative choices and shaped the direction of the branding system.',
          },
          {
            type: 'ol',
            items: [
              'The data centre assembly industry (modular vs. traditional engineering)',
              'Competitor audit (given to participants as modular power solutions)',
              '“Cousin” audit (as MOSM was considered the “Foxconn solutions” of modular data centre manufacturing)',
            ],
          },
        ],
      },
      {
        id: 'final-designs',
        label: 'final designs',
        blocks: [
          {
            type: 'p',
            text: 'See my final design choices below :)',
          },
          {
            type: 'figmaEmbed',
            url:
              'https://www.figma.com/design/zemR1hCHcTORqkzpJFrZUp/modularosm-%7C-portfolio-ver.?node-id=0-1&t=Et8iJKzkPgieU0gu-0',
          },
        ],
      },
      {
        id: 'reflections',
        label: 'reflections',
        blocks: [
          {
            type: 'p',
            text:
              "I'm excited to share that I was a top 5 finalist at the UW/UX origins case competition!",
          },
          {
            type: 'p',
            text:
              "This was my first solo design competition, and one of the most rewarding creative challenges I've taken on. After some 20+ hours of work, I worked on brand strategy, logo concepting, visual systems, and website design. I learned so much about designing with intention, communicating your vision, and how to build a branding system from the ground up.",
          },
          {
            type: 'p',
            text:
              'Thank you @uw_ux and Tony Ding (Nexxus) for organizing the origin case competition!',
          },
          {
            type: 'img',
            src: '/media/mosm-origin-finalist.png',
            alt:
              'Banner celebrating top 5 finalist placement at the UW/UX ORIGIN brand design case competition',
            href:
              'https://www.linkedin.com/posts/amanda-zhu-b01422242_uwux-uxdesign-casecompetition-activity-7439731185737510912-NWlU?utm_source=share&utm_medium=member_desktop&rcm=ACoAADw4BbEBz1P-6Y7gkabqvGfqZUaNXtWhAMQ',
          },
        ],
      },
    ],
  },

  wingmate: {
    slug: 'wingmate',
    headline: 'dating for people fresh into the world',
    heroGradient: 'linear-gradient(135deg, #fef3e8 0%, #fde8d4 45%, #f97316 100%)',
    heroInner: {
      type: 'img',
      src: '/media/wingmate-thumbnail.svg',
      alt: 'Wingmate high-fidelity mobile mockups',
    },
    meta: {
      role: 'team lead &\nproduct designer',
      team: 'elliott vince\nteagan wright\n& myself',
      skills: 'illustration\nuser research\nui/ux design\nproduct design',
      timeline: 'jan - march 2026',
    },
    sections: [
      {
        id: 'overview',
        label: 'overview',
        blocks: [
          {
            type: 'p',
            text:
              'Dating apps have become one of the primary ways people find romantic connections. While they allow people to easily access a wide range of potential partners, many users report frustration and negative experiences in the dating app world.',
          },
          {
            type: 'p',
            text:
              'tldr: three single university students who have never used any dating apps ever, came together to design a dating app!',
          },
          {
            type: 'img',
            src: '/media/wingmate-illustration.svg',
            alt: 'Wingmate product illustration',
          },
        ],
      },
      {
        id: 'problem-statement',
        label: 'problem statement',
        blocks: [
          {
            type: 'p',
            text:
              'When the needs for honesty, effort, and alignment in relationship goals is not being met, it can feel very time-consuming, frustrating, inefficient and emotionally draining if the user is looking to find a genuine and meaningful connection.',
          },
          { type: 'h3', text: 'user pain points' },
          {
            type: 'toggles',
            items: [
              {
                title: '1 misrepresentation or “catfishing”.',
                body:
                  'Users frequently encounter profiles that exaggerate, mislead, or completely fake personal information. This creates a lack of trust and makes users feel much more skeptical and reluctant to interact with others on the app.',
              },
              {
                title: '2 low effort put into profile making.',
                body:
                  'Interviewees felt that profiles they were seeing contained poor photos, very sparse or empty bios, and information that raised immediate “red flags”. This has led to feelings of frustration and discouragement from the endless scroll through profiles that don’t align with their interest, values, or expectations.',
              },
              {
                title: '3 finding partners with similar relationship goals.',
                body:
                  'A common concern expressed by interviewees included misaligned intentions, as many people using existing dating apps are primarily looking for something casual or hookups. Users looking for long-term or a more serious relationship struggle to find matches with the same goal.',
              },
            ],
          },
        ],
      },
      {
        id: 'the-design-process',
        label: 'the design process',
        blocks: [
          {
            type: 'p',
            text:
            'at each point in the design process (ideation, low-fi, high-fi), we conducted user interviews to validate our design choices and ensure the app was meeting the needs of the users. a report was created at each stage to document the process, including updated problem statements,user scenarios, user feedback, etc.',
          },
          {
            type: 'img',
            src: '/media/wingmate-report.svg',
            alt: 'Wingmate design process report',
          },
        ],
      },
      {
        id: 'user-research',
        label: 'user research',
        blocks: [
          {
            type: 'p',
            text:
              'To understand the pain points of modern dating app users, we conducted structured interviews with five participants aged 21–27, spanning a mix of genders and relationship statuses. Interviews explored both positive and negative experiences with existing dating apps, as well as personal relationship goals and expectations. From these conversations, three distinct target user groups emerged: young singles actively seeking committed relationships, people already in relationships who want to help their friends find love, and singles who fit both roles. Three personas (Sophie, Mark & Kathy) were created to each represent a unique perspective on dating, connection, and the frustrations of existing platforms.',
          },
        ],
      },
      {
        id: 'low-fi-paper-prototype',
        label: 'low-fi paper prototype',
        blocks: [
          {
            type: 'p',
            text:
              'We conducted four user evaluations for our low-fi paper prototype. There were three females and one male. All were between 20-22 years old, heterosexual, and were of various ethnicities. We gave them a brief description of Wingmate. Then, we let them interact with our paper prototype. We encouraged them to ask questions or clarifications as needed. We recorded the audio and video for each interview so we could study their reactions to our prototype. Our only instruction was for them to interact with the app as if it were a real app. Our interviews lasted between five and ten minutes each. After each demo, we asked follow-up questions. First, we had them describe any parts that did not make sense. Then, we gauged their satisfaction with the profiles and asked what could be improved. Lastly, we asked how they would feel about using Wingmate while in a relationship.',
          },
          {
            type: 'video',
            src: '/media/wingmate-lofi.mov',
            autoplay: true,
          },
        ],
      },
      {
        id: 'final-designs',
        label: 'high-fi prototype',
        blocks: [
          {
            type: 'p',
            text:
              'Post user testing, we made some final design choices to ensure the app was meeting the needs of the users. We kept the design choices intentionally simple. A palette of off-black, off-white, and warm accent colours ensures the interface stays clean and uncluttered, keeping the focus on what matters most: the people using it!',
          },
          {
            type: 'figmaEmbed',
            url:
              'https://www.figma.com/design/EOQ0MQwHFijnZCyGb74ZAx/wingmate-%7C-high-fi-prototype?node-id=0-1&t=grplehgFTmaFA2LI-1',
          },
        ],
      },
      {
        id: 'reflections',
        label: 'reflections',
        blocks: [
          {
            type: 'p',
            text:
              'This was my first UX design project involving paper prototyping, and while it was very time consuming, it turned out to be one of the most valuable tools in our feedback process.',
          },
          {
            type: 'p',
            text:
              'I could not have asked for a better team of friends to work with (thank you Elliott & Teagan), and with the high-fi designs done, maybe we\'ll actually ship Wingmate someday!',
          },
        ],
      },
    ],
  },

  hollow: {
    slug: 'hollow',
    headline: 'user research is easy; when the user is me',
    heroGradient:
      'linear-gradient(135deg, #f8f6f6 0%, #e8e3dd 38%, #aca79f 65%, #bc8c82 100%)',
    heroInner: {
      type: 'img',
      src: '/media/ppa.svg',
      alt: 'Perfect productivity app — product thumbnail',
    },
    meta: {
      role: 'ui/ux, app design',
      team: 'personal project',
      skills: 'strategy direction, figma prototyping',
      timeline: 'jan 2026 - current',
    },
    sections: [
      {
        id: 'overview',
        label: 'overview',
        blocks: [
          {
            type: 'p',
            text:
              'i have used way too many different productivity apps in my life. i wanted to design an app that was simple, aesthetically pleasing, and optimized for my own needs.'
          },
        ],
      },
      {
        id: 'user research',
        label: 'user research',
        blocks: [{ type: 'p', text: 'user research is easy; when the only user is me!' }],
      },
      {
        id: 'case study',
        label: 'case study',
        blocks: [{ type: 'p', text: 'coming soon ... ♬' }],
      },
    ],
  },
};

export function getCaseStudy(slug: string | undefined): CaseStudyDoc | undefined {
  if (!slug) return undefined;
  return CASE_STUDIES[slug];
}
